import PropTypes from 'prop-types';
import React from 'react';
import {
  Check,
  MessageCircle,
  Sparkles,
  TrendingUp,
  UserPlus,
} from 'lucide-react';

import styles from './JobActionCards.module.scss';
import { trackEvent } from 'helpers/Analytics';

const JobActionCards = ({
  publicQuestions,
  isBoosted,
  boostDaysRemaining,
  isProfileComplete,
  profileStepsCompleted,
  profileStepsTotal,
  profileCompletionPercentage,
  freelancersInvited,
  freelancersTarget,
  freelancerTargetHit,
  browseURL,
  aiRecruiterEnabled,
  onQuestionsClick,
  onNextStepsClick,
  onToggleAIRecruiter,
}) => {
  const unansweredQuestions =
    publicQuestions?.filter((q) => !q.answer || q.answer.trim() === '')
      .length || 0;

  return (
    <div
      className={styles.actionCardsSection}
      data-walkthrough="ats-job-actions"
    >
      <div className={styles.actionCardsGrid}>
        {/* Public Questions Card */}
        <button className={styles.actionCard} onClick={onQuestionsClick}>
          <div
            className={`${styles.actionCardBadge} ${
              unansweredQuestions === 0 ? styles.actionCardBadgeInactive : ''
            }`}
          >
            {unansweredQuestions}
          </div>
          <MessageCircle width={20} height={20} />
          <div className={styles.actionCardContent}>
            <div className={styles.actionCardTitle}>Questions</div>
            <div className={styles.actionCardSubtitle}>
              {unansweredQuestions > 0
                ? `${unansweredQuestions} awaiting your reply`
                : 'No pending questions'}
            </div>
          </div>
        </button>

        {/* Boosted Card */}
        <a
          className={styles.actionCard}
          href="https://www.twine.net/enterprise"
          target="_blank"
          rel="noreferrer"
          onClick={() => trackEvent('client-ats-clicks-boost-job')}
        >
          <div
            className={`${styles.actionCardBadge} ${
              isBoosted
                ? styles.actionCardBadgeActive
                : styles.actionCardBadgeInactive
            }`}
          >
            {isBoosted ? 'On' : 'Off'}
          </div>
          <TrendingUp
            width={24}
            height={24}
            className={`${styles.actionCardIcon} ${
              isBoosted
                ? styles.boostedActionCardIconActive
                : styles.boostedActionCardIconInactive
            }`}
          />

          <div className={styles.actionCardContent}>
            <div className={styles.actionCardTitle}>Boost Job</div>
            <div
              className={`${styles.actionCardSubtitle} ${
                isBoosted ? styles.actionCardSubtitleActive : ''
              }`}
            >
              {isBoosted
                ? `Active • ${boostDaysRemaining} day${
                    boostDaysRemaining !== 1 ? 's' : ''
                  } left`
                : 'Increase visibility'}
            </div>
          </div>
        </a>

        {/* Next Steps / Invite Freelancers Card */}
        {isProfileComplete ? (
          <a
            {...(freelancersInvited < freelancersTarget
              ? {
                  href: browseURL,
                  target: '_blank',
                  rel: 'noreferrer',
                }
              : {})}
            onClick={(e) => {
              if (freelancerTargetHit) {
                e.preventDefault();
              }
            }}
            className={`${styles.actionCard} ${
              freelancerTargetHit ? styles.actionCardDisabled : ''
            }`}
          >
            <div
              className={`${styles.actionCardBadge} ${
                freelancerTargetHit
                  ? styles.actionCardBadgeActive
                  : styles.actionCardBadgeInfo
              }`}
            >
              {freelancersInvited}/{freelancersTarget}
            </div>
            <div
              className={`${styles.actionCardIcon} ${
                freelancerTargetHit
                  ? styles.inviteActionCardIconComplete
                  : styles.inviteActionCardIconActive
              }`}
            >
              {freelancerTargetHit ? (
                <Check width={20} height={20} />
              ) : (
                <UserPlus width={20} height={20} />
              )}
            </div>
            <div className={styles.actionCardContent}>
              <div className={styles.actionCardTitle}>
                {freelancerTargetHit ? 'Invited' : 'Invite Freelancers'}
              </div>
              <div className={styles.actionCardSubtitle}>
                {freelancersInvited} of {freelancersTarget} invited
              </div>
            </div>
          </a>
        ) : (
          <button onClick={onNextStepsClick} className={styles.actionCard}>
            <div
              className={`${styles.actionCardBadge} ${
                profileStepsCompleted === profileStepsTotal
                  ? styles.actionCardBadgeActive
                  : styles.actionCardBadgeInfo
              }`}
            >
              {profileCompletionPercentage}%
            </div>
            <div className={styles.circularProgress}>
              <svg
                className={styles.circularProgressSvg}
                viewBox="0 0 24 24"
                width="20"
                height="20"
              >
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  fill="none"
                  stroke="#e5e7eb"
                  strokeWidth="3"
                />
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  fill="none"
                  stroke="#2eb85c"
                  strokeWidth="3"
                  strokeDasharray={`${2 * Math.PI * 10}`}
                  strokeDashoffset={`${
                    2 *
                    Math.PI *
                    10 *
                    (1 - profileStepsCompleted / profileStepsTotal)
                  }`}
                  strokeLinecap="round"
                />
              </svg>
            </div>
            <div className={styles.actionCardContent}>
              <div className={styles.actionCardTitle}>Next Steps</div>
              <div className={styles.actionCardSubtitle}>
                {profileStepsCompleted} of {profileStepsTotal} completed
              </div>
            </div>
          </button>
        )}

        {/* AI Recruiter Card */}
        <button className={styles.actionCard} onClick={onToggleAIRecruiter}>
          <div
            className={`${styles.actionCardBadge} ${
              aiRecruiterEnabled
                ? styles.actionCardBadgeActive
                : styles.actionCardBadgeInactive
            }`}
          >
            {aiRecruiterEnabled ? 'On' : 'Off'}
          </div>
          <Sparkles
            width={20}
            height={20}
            className={`${styles.actionCardIcon} ${
              aiRecruiterEnabled
                ? styles.recruiterActionCardIconActive
                : styles.recruiterActionCardIconInactive
            }`}
          />
          <div className={styles.actionCardContent}>
            <div className={styles.actionCardTitle}>AI Recruiter</div>
            <div className={styles.actionCardSubtitle}>
              {aiRecruiterEnabled
                ? 'Auto-screening active'
                : 'Enable automation'}
            </div>
          </div>
        </button>
      </div>
    </div>
  );
};

JobActionCards.propTypes = {
  publicQuestions: PropTypes.array,
  isBoosted: PropTypes.bool.isRequired,
  boostDaysRemaining: PropTypes.number.isRequired,
  isProfileComplete: PropTypes.bool.isRequired,
  profileStepsCompleted: PropTypes.number.isRequired,
  profileStepsTotal: PropTypes.number.isRequired,
  profileCompletionPercentage: PropTypes.number.isRequired,
  freelancersInvited: PropTypes.number.isRequired,
  freelancersTarget: PropTypes.number.isRequired,
  freelancerTargetHit: PropTypes.bool.isRequired,
  browseURL: PropTypes.string.isRequired,
  aiRecruiterEnabled: PropTypes.bool.isRequired,
  onQuestionsClick: PropTypes.func.isRequired,
  onNextStepsClick: PropTypes.func.isRequired,
  onToggleAIRecruiter: PropTypes.func.isRequired,
};

export default JobActionCards;
